/**
 * Dev labels — debug-only `data-dev` / `data-dev-panel` attributes that mark
 * every UI surface in the editor. Used together with `<DevLabelToggle />` to
 * visualize panel boundaries via CSS (body.dev-labels-on) on D-key press.
 *
 * In production (NODE_ENV === "production") these helpers return empty objects
 * so the attributes never reach the user's HTML — zero bytes added to the
 * production bundle, zero attributes added to rendered DOM.
 *
 * Source-of-truth panel IDs live in `docs/mockups/*.html`. Never invent new IDs
 * here — copy them from the mockup HTML (e.g. data-dev="1-A", data-dev-panel="4").
 *
 * Phase 25-AB Task 2.
 *
 * @example
 *   import { devLabel, devLabelPanel } from "@/lib/dev-labels";
 *
 *   <div {...devLabelPanel("4")}>...</div>
 *   <button {...devLabel("1-A")}>Render</button>
 */

const isEnabled = (): boolean => process.env.NODE_ENV !== "production";

/**
 * Returns `{ "data-dev": id }` in non-production, `{}` in production.
 * Spread onto a JSX element to mark it for debug visualization.
 */
export function devLabel(id: string): Record<string, string> {
	return isEnabled() ? { "data-dev": id } : {};
}

/**
 * Returns `{ "data-dev-panel": id }` in non-production, `{}` in production.
 * Use for panel-level (outermost wrapper of a layout region) markers.
 */
export function devLabelPanel(id: string): Record<string, string> {
	return isEnabled() ? { "data-dev-panel": id } : {};
}

/**
 * Strip `data-dev` and `data-dev-panel` attributes from an HTML string.
 *
 * Handles single- or double-quoted values. Leaves other `data-*` attributes
 * untouched. Collapses leftover whitespace inside the opening tag.
 *
 * Used in SSR / static-HTML generation tests to assert production output
 * does not contain dev labels.
 */
export function stripDevLabels(html: string): string {
	return html
		.replace(/\s*data-dev(?:-panel)?=("|')[^"']*\1\s*/g, " ")
		.replace(/\s+>/g, ">")
		.replace(/<(\w+)\s+>/g, "<$1>");
}
